import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import apiClient from "@/app/api/client";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import type { PaginatedApiResponse } from "@/shared/types/pagination";
import { usersService } from "../services/users.service";
import type { CreateUserInput, ManagedUserListItem, RoleOption, UpdateUserInput } from "../types";

export function useUsersList(filters: { phone?: string }) {
	return usePaginatedQuery<ManagedUserListItem>({
		queryKey: ["users"],
		url: "/users",
		params: filters.phone ? { phone: filters.phone } : undefined,
	});
}

/**
 * `roleId` Select uchun rollar ro'yxati — `UserFormModal` (yaratish) va
 * `AssignRoleModal` (almashtirish) ikkalasi ham shu hookdan foydalanadi.
 * `staleTime` yo'q — `RolesPage` mutatsiyalari `["roles"]` prefiksini
 * invalidatsiya qiladi va bu kalit ham shu prefiks ostida.
 */
export function useRoleOptions(enabled = true) {
	return useQuery({
		queryKey: ["roles", "options"],
		queryFn: async () => {
			const response = await apiClient.get<PaginatedApiResponse<RoleOption>>("/roles", {
				params: { page: 1, limit: 100 },
			});
			return response.data.data.items;
		},
		enabled,
	});
}

function useUsersMutation<TVariables>(
	mutationFn: (variables: TVariables) => Promise<unknown>,
	successMessage: string
) {
	const { message } = App.useApp();
	const { t } = useTranslation("users");
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
			message.success(successMessage);
		},
		onError: () => {
			message.error(t("messages.actionFailed"));
		},
	});
}

export function useCreateUser() {
	const { t } = useTranslation("users");
	return useUsersMutation(
		(input: CreateUserInput) => usersService.create(input),
		t("messages.created")
	);
}

export function useUpdateUser() {
	const { t } = useTranslation("users");
	return useUsersMutation(
		({ id, input }: { id: string; input: UpdateUserInput }) => usersService.update(id, input),
		t("messages.updated")
	);
}

export function useDeleteUser() {
	const { t } = useTranslation("users");
	return useUsersMutation((id: string) => usersService.remove(id), t("messages.deleted"));
}

export function useAssignRole() {
	const { t } = useTranslation("users");
	return useUsersMutation(
		({ subjectId, roleId, scope }: { subjectId: string; roleId: string; scope?: string }) =>
			usersService.assignRole(subjectId, roleId, scope),
		t("messages.roleAssigned")
	);
}

export function useRevokeRole() {
	const { t } = useTranslation("users");
	return useUsersMutation(
		({ subjectId, roleId }: { subjectId: string; roleId: string }) =>
			usersService.revokeRole(subjectId, roleId),
		t("messages.roleRevoked")
	);
}
